import React from "react";
import { useForm, FieldError } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "../common/Modal";
import Input from "../common/Input";
import Button from "../common/Button";
import {
  playlistTagCreateSchema,
  playlistTagUpdateSchema,
  PlaylistTagFormData,
  PlaylistTagUpdateFormData,
  PlaylistTag,
} from "../../schemas/playlistTagSchema";

interface PlaylistTagFormProps {
  isOpen: boolean;
  initialData?: PlaylistTagFormData | PlaylistTagUpdateFormData | PlaylistTag;
  onSubmit: (data: PlaylistTagFormData | PlaylistTagUpdateFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  isUpdate?: boolean;
}

const PlaylistTagForm: React.FC<PlaylistTagFormProps> = ({
  isOpen,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  isUpdate = false,
}) => {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    trigger,
  } = useForm<PlaylistTagFormData | PlaylistTagUpdateFormData>({
    resolver: zodResolver(isUpdate ? playlistTagUpdateSchema : playlistTagCreateSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialData
      ? {
          name: (initialData as PlaylistTag).name || (initialData as PlaylistTagFormData).name || "",
          description: (initialData as PlaylistTag).description || (initialData as PlaylistTagFormData).description || "",
        }
      : { name: "", description: "" },
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      title={initialData ? "Edit Playlist Tag" : "Create Playlist Tag"}
      size="md"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
        <Input
          label="Name"
          placeholder="Enter name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name as FieldError | undefined}
        />

        <Input
          label="Description"
          placeholder="Optional"
          name="description"
          value={watch("description") || ""}
          onChange={(e) => setValue("description", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("description")}
          error={errors.description as FieldError | undefined}
        />

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={isLoading}>
            {initialData ? "Update" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PlaylistTagForm;
